import React, {useCallback, useEffect} from 'react'
import {isDefined} from '../../common/types/helpers'
import {Form, FormSpacer} from '../../components/form'
import {NumberInput} from '../../components/library/form/number-input'
import {Icon} from '../../components/library/icon/icon'
import styled from 'styled-components'
import {Switch} from '../../components/library/toggles/switch'
import {
  useGetOrganisationQuery,
  useUpdateOrganisationPurgeDaysMutation
} from '../../data/workbench-api/generated/organisation'
import {useStateChanged} from '../../hooks/use-state-changed'
import {useToast} from '../../lib/toast'
import {ConstrainWidth, IconHeading} from './components'

const Description = styled.div`
  color: ${props => props.theme.grey50};

  ul {
    padding-left: 15px;
    li {
      padding: 0;
      margin: 0 0 5px;
    }
  }
`

export const DataRetention = ({
  organisationId,
  canEdit
}: {
  organisationId: string
  canEdit: boolean
}) => {
  const toaster = useToast()
  const orgQuery = useGetOrganisationQuery({
    variables: {id: organisationId}
  })

  const [doMutation, {loading: updating, error: updateError}] =
    useUpdateOrganisationPurgeDaysMutation({
      onCompleted: () => toaster('Organisation data retention updated')
    })

  const [localState, setLocalState, changed, resetState] = useStateChanged<{
    dataRetentionPeriod: number | undefined
    enabled: boolean
  }>({dataRetentionPeriod: undefined, enabled: false})

  useEffect(() => {
    if (!orgQuery.loading) {
      resetState({
        dataRetentionPeriod:
          orgQuery.data?.organisation?.purgeDays ?? undefined,
        enabled: isDefined(orgQuery.data?.organisation?.purgeDays)
      })
    }
  }, [orgQuery, resetState])

  const resetForm = useCallback(() => {
    if (!orgQuery.loading) {
      resetState({
        dataRetentionPeriod:
          orgQuery.data?.organisation?.purgeDays ?? undefined,
        enabled: isDefined(orgQuery.data?.organisation?.purgeDays)
      })
    }
  }, [orgQuery, resetState])

  const updateDataRetention = (input: {
    dataRetentionPeriod: number | undefined | null
  }) =>
    doMutation({
      variables: {
        id: organisationId,
        purgeDays: input.dataRetentionPeriod
      }
    })

  const loading = updating || orgQuery.loading
  const valid: boolean =
    !localState.enabled ||
    (!!localState.dataRetentionPeriod &&
      localState.dataRetentionPeriod > 0 &&
      localState.enabled)

  return (
    <>
      <header>
        <IconHeading>
          <Icon name="date" />
          Data Retention
        </IconHeading>
      </header>

      <ConstrainWidth>
        <Description>
          <p>
            If enabled, all records older than the specified number of days will
            be <strong>permanently</strong> deleted, including:
          </p>
          <ul>
            <li>Audit logs</li>
            <li>Analytic batches & events</li>
            <li>Completed Action Cards and Action Flows</li>
            <li>SDK request debugger logs</li>
            <li>Webhook logs</li>
          </ul>
        </Description>
        <Form
          loading={loading}
          onCancel={changed ? () => resetForm() : undefined}
          onSubmit={() =>
            updateDataRetention({
              dataRetentionPeriod: localState.enabled
                ? localState.dataRetentionPeriod
                : null
            })
          }
          error={updateError ?? orgQuery.error}
          submitText="Save"
          isValid={changed && !loading && valid}
        >
          <FormSpacer />
          <Switch
            label="Enable data removal"
            checked={localState.enabled}
            disabled={!canEdit || loading}
            onChange={enabled =>
              setLocalState(c => ({
                ...c,
                dataRetentionPeriod: c.dataRetentionPeriod,
                enabled
              }))
            }
            isForm={true}
            labelPosition="left"
          />
          <FormSpacer />
          <NumberInput
            style={{maxWidth: '100%', marginBottom: '20px'}}
            required={false}
            disabled={!canEdit || loading || !localState.enabled}
            type="number"
            label="Number of days to keep any data..."
            upLabel="Retention Period (Days)"
            value={
              localState.enabled ? localState.dataRetentionPeriod : undefined
            }
            min={'1'}
            onChange={retention =>
              setLocalState(c => ({
                ...c,
                dataRetentionPeriod: retention
              }))
            }
          />
        </Form>
      </ConstrainWidth>
    </>
  )
}
